import { defineElement, FC, onDidLoad, useElement, useListen, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { elements } from '@source/utilities/dom';
import {
    addModal,
    closeModal,
    dismissCloseModal,
    modalStore,
    requestCloseModal,
} from '@store/modals';
import { useStylesheet } from '@utilities/hooks';

import style from './modal.scss';

const MODAL_HOOK = '[js-hook-modal]';
const MODAL_VISIBLE_CLASS = 'modal--is-showing';
const MODAL_HTML_CLASS = 'modal--is-open';

export interface Modal extends HTMLElement {
    closeDisabled: boolean;
    active: boolean;
    hasShadow: boolean;
    hasConfirmationPopup: boolean;
}

const BpdModal: FC<Modal> = ({ element }) => {
    useStylesheet(style);
    useProp<boolean>('closeDisabled', false);
    useProp<boolean>('hasShadow', false);
    useProp<boolean>('hasConfirmationPopup', false);
    const [, , watchActive] = useProp<boolean>('active', false);
    const [onModalClose] = useProp<Function>('onModalClose', () => null);

    const modal = useElement<HTMLDivElement>(MODAL_HOOK);

    const isActiveEvent = new Event(`modalIsActive::${element.id}`);
    const isInactiveEvent = new Event(`modalIsInactive::${element.id}`);

    function handleCloseModal() {
        if (element.hasConfirmationPopup) {
            const modalCloseRequest = modalStore.getState().modals[element.id].closeRequest;
            modalCloseRequest ? dismissCloseModal(element.id) : requestCloseModal(element.id);
        } else {
            closeModal(element.id);
        }
    }

    watchActive(handleActiveChange);
    function handleActiveChange(isActive: boolean) {
        const action = isActive ? 'add' : 'remove';
        modal.current?.classList[action](MODAL_VISIBLE_CLASS);
        elements.html.classList[action](MODAL_HTML_CLASS);

        if (isActive) {
            document.dispatchEvent(isActiveEvent);
        } else {
            onModalClose();
            document.dispatchEvent(isInactiveEvent);
        }
    }

    useListen(document, 'keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') handleCloseModal();
    });

    onDidLoad(() => {
        // Open modal based upon active state
        handleActiveChange(element.active);

        if (element.id) {
            // Add modal to the store based upon the modal id
            addModal(element.id);

            // Subscribe to the store to know when a modal is changed.
            modalStore.subscribe(({ modals }) => {
                const { active } = modals[element.id];
                element.active = active;
            });
        }
    });

    return (
        <div class="c-modal" js-hook-modal>
            <div
                class={`modal__container${
                    element.hasShadow ? ' modal__container--has-shadow' : ''
                }`}>
                <div class="modal__content" role="dialog">
                    <slot />
                </div>
            </div>
            <div class="modal__background" role="button" tabindex="0" onClick={handleCloseModal} />
        </div>
    );
};

BpdModal.props = {
    closeDisabled: {
        type: Boolean,
        reflectToAttr: true,
    },
    active: {
        type: Boolean,
        reflectToAttr: true,
    },
    hasShadow: {
        type: Boolean,
        reflectToAttr: true,
    },
    hasConfirmationPopup: {
        type: Boolean,
        reflectToAttr: true,
    },
    onModalClose: {
        type: Object,
    },
};

defineElement('bpd-modal', BpdModal, { useShadowDom: true });
